import { useMemo } from "react";
import { FileText, Calendar, ListOrdered } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface DocumentItem {
  id: string;
  label: string;
}

interface CoverPagePreviewProps {
  title: string;
  documents: DocumentItem[];
  className?: string;
}

export function CoverPagePreview({ title, documents, className = "" }: CoverPagePreviewProps) {
  const dateStr = useMemo(() => {
    return new Date().toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  }, []);

  return (
    <div className={`relative ${className}`}>
      {/* A4 aspect ratio container with shadow to look like a real page */}
      <div 
        className="bg-white border border-border rounded-lg shadow-lg overflow-hidden"
        style={{ aspectRatio: '210 / 297' }} // A4 ratio
      >
        {/* Scale down content to fit preview */}
        <div className="p-4 h-full flex flex-col text-[10px] sm:text-xs">
          {/* Header line */}
          <div className="h-0.5 bg-primary rounded-full mb-3" />
          
          {/* Title section */}
          <div className="mb-2">
            <span className="text-muted-foreground uppercase tracking-wider text-[8px] sm:text-[9px]">
              Dossier
            </span>
          </div>
          
          <h2 className="font-bold text-foreground text-sm sm:text-base leading-tight mb-2 line-clamp-3">
            {title}
          </h2>
          
          {/* Date */}
          <div className="flex items-center gap-1 text-muted-foreground mb-3">
            <Calendar className="w-2.5 h-2.5 sm:w-3 sm:h-3" />
            <span className="text-[8px] sm:text-[9px]">Généré le {dateStr}</span>
          </div>
          
          {/* Separator */}
          <div className="h-px bg-border mb-3" />
          
          {/* Documents list header */}
          <div className="flex items-center gap-1.5 mb-2">
            <ListOrdered className="w-3 h-3 text-primary" />
            <span className="font-semibold text-muted-foreground uppercase tracking-wider text-[8px] sm:text-[9px]">
              Documents inclus
            </span>
          </div>
          
          {/* Documents list */}
          <div className="flex-1 overflow-hidden">
            <div className="space-y-1">
              {documents.slice(0, 8).map((doc, index) => (
                <div key={doc.id} className="flex items-start gap-1.5">
                  <span className="font-bold text-primary text-[8px] sm:text-[9px] min-w-[16px]">
                    {String(index + 1).padStart(2, '0')}.
                  </span>
                  <span className="text-foreground text-[8px] sm:text-[9px] line-clamp-1">
                    {doc.label}
                  </span>
                </div>
              ))}
              {documents.length > 8 && (
                <div className="text-muted-foreground text-[8px] sm:text-[9px] italic pl-5">
                  + {documents.length - 8} autre{documents.length - 8 > 1 ? 's' : ''} document{documents.length - 8 > 1 ? 's' : ''}...
                </div>
              )}
            </div>
          </div>
          
          {/* Footer */}
          <div className="mt-auto pt-2 border-t border-border">
            <div className="flex items-center justify-between text-muted-foreground">
              <div className="flex items-center gap-1">
                <FileText className="w-2.5 h-2.5" />
                <span className="text-[7px] sm:text-[8px]">
                  {documents.length} document{documents.length > 1 ? 's' : ''}
                </span>
              </div>
              <span className="text-[7px] sm:text-[8px]">
                Généré par EasyFinder
              </span>
            </div>
          </div>
        </div>
      </div>
      
      {/* Preview badge */}
      <Badge 
        variant="secondary" 
        className="absolute -top-2 -right-2 text-[9px] px-1.5 py-0.5 shadow-sm"
      >
        Aperçu
      </Badge>
    </div>
  );
}
